import {
  FeatureLayer,
  SimpleMarkerSymbol,
  Point,
  GraphicsLayer,
  Graphic,
  esriProjection,
  SpatialReference,
  SimpleRenderer,
  MapView,
  FeatureSet,
} from 'cd-eft/utils/esriTypes';
import {
  LinearUnit,
  constructRectPitLayer,
  constructRoundPitLayer,
  constructDikedPitLayer,
} from '../../utils/structureLayerUtils';
import { action, observable } from 'mobx';
import { ProjectsStore } from './ProjectsStore';
import Layer from '@arcgis/core/layers/Layer';
import { appConfig } from 'cd-eft/config/AppConfig';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import Polyline from '@arcgis/core/geometry/Polyline';
import * as eftSetApi from 'cd-eft/_codegen_/openapi-generator/eft-set';
import { putStructureRepOnTop, drawRectangularBase, drawCircleBase } from 'cd-eft/utils/gisUtils';
import { commonFields } from 'cd-eft/utils/gisUtils';
import { INotification } from 'cd-eft/models/Stores/DesignStore';

class PitDesignProjectsStore extends ProjectsStore {
  constructor(private pitdesignstore: any) {
    super(pitdesignstore);
  }
}

export class PitDesignStore {
  constructor(private appStore: any) {
    this.projectsStore = new PitDesignProjectsStore(this);
    this.init();
  }

  private _currentWkid = appConfig.tempHardCodes.wkid;

  @observable projectsStore: PitDesignProjectsStore;
  @observable hasGrid: boolean = false;
  @observable hasDesign: boolean = false;
  @observable editDesign: boolean = false;
  @observable newPit!: INewPit;
  @observable newDesignForm!: INewPitForm;
  @observable chooseCenter: boolean = false;
  @observable newPitResults: any = {};
  @observable pitSummaryTable: any;
  @observable projectMetadata;
  @observable selectedPitsInfo: any;
  @observable displayedPitsIds: number[] = [];
  @observable addPitModalOpen: boolean = false;
  @observable latestElementId?: number;
  @observable showLoader: boolean = false;
  @observable surveyListDataGridTabs = ['Pit Summary', 'New Pit'];
  @observable currentTab: string = 'Pit Summary';
  @observable newDesign: boolean = false;
  @observable pitLayers: Layer[] = [];
  @observable dikedPitLayers: Layer[] = [];
  @observable currentDesignLayer: GroupLayer = new GroupLayer();
  @observable originalDesignLayer: GroupLayer | undefined;
  @observable originalPitResults: any = {};
  @observable originalIdString: string = '';
  @observable pendingEdits: IPendingEdits = { name: false, design: false, unsavedEdits: true };
  @observable notification: INotification = {
    message: '',
    header: '',
    type: '',
    hidden: true,
    timestamp: undefined,
  };

  @action.bound showNotification(msg: string, header: string, type: string = 'error') {
    this.notification = {
      message: msg,
      header: header,
      type: type,
      hidden: false,
      timestamp: new Date(),
    };
  }

  @action.bound clearNotification() {
    this.notification = { message: '', header: '', type: '', hidden: true, timestamp: undefined };
  }

  @action changeTab(tab) {
    this.currentTab = tab;
  }

  @action reset() {
    this.init();
  }

  @action init() {
    this.resetNewPitForm();
    this.clearNotification();
    this.pitSummaryTable = [];
    this.selectedPitsInfo = [];
    this.displayedPitsIds = [];
    this.addPitModalOpen = false;
    this.chooseCenter = false;
    this.hasDesign = false;
    this.editDesign = false;
    this.chooseCenter = false;
    this.addPitModalOpen = false;
    this.showLoader = false;
    this.newDesign = false;
    this.currentTab = 'Pit Summary';
    this.newPit = {
      pitName: '',
      pitType: '',
    };

    this.projectMetadata = {
      projectName: '',
      surfaceName: '',
      surfaceType: '',
    };
    this.hasGrid = false;
    this.pitLayers = [];
    this.addPitModalOpen = false;
  }

  @action resetNewPitForm() {
    this.newDesignForm = {
      name: '',
      type: '',
      equalSlopes: true,
      equalCutSlopes: true,
      equalFillSlopes: true,
      equalSideSlopes: true,
      center: {
        pointName: 'center',
        x: 0,
        y: 0.0,
        z: 0,
      },
      width: 10,
      length: 10,
      topWidth: 10,
      topElevation: 0,
      bottomWidth: 10,
      bottomElevation: 0,
      bottomLength: 10,
      pitDepth: 0,
      orienAzimuth: 0,
      radius: 0,
      designElementId: 0,
      slopes: [4, 4, 4, 4],
      cutSlopes: [4, 4, 4, 4],
      fillSlopes: [4, 4, 4, 4],
      sideSlopes: [4, 4, 4, 4],
    };
    this.pendingEdits = {
      name: false,
      design: false,
      unsavedEdits: true
    };
  }

  @action cancelDesignElement() {
    console.warn('cancelDesignElement');
    this.changeTab('Pit Summary');
    this.newPitResults = {};
    this.hasDesign = false;
    this.editDesign = false;
    this.newDesign = false;
    this.newDesignForm.type = '';
    this.originalDesignLayer = undefined;
    this.originalIdString = '';
    this.originalPitResults = undefined;
    this.resetNewPitForm();
  }

  @action addPitToMap(pit: any, mapView: MapView, isCurrentDesignLayer: boolean, projectSR: SpatialReference, id?: string, label?: any): Layer {
    const pitLayer = this.createPitLayer(pit, mapView, projectSR, id, label);
    if (id) {
      pitLayer.id = id;
    }
    if (isCurrentDesignLayer && this.currentDesignLayer) {
      mapView.map.remove(this.currentDesignLayer);
    }
    mapView.map.add(pitLayer);
    if (isCurrentDesignLayer) {
      this.currentDesignLayer = pitLayer;
      if (id) {
        const pitEntry = (this.pitSummaryTable as any[]).find((entry) => entry.designElementId === id);
        const layerIndex = this.pitLayers.findIndex((layer) => layer.id === id.toString());
        if (layerIndex !== -1) {
          this.pitLayers.splice(layerIndex, 1);
        }
        if (pitEntry && pitEntry.checked) this.pitLayers.push(pitLayer);
      }
    } else {
      this.pitLayers.push(pitLayer);
    }
    // Ensure that the currentDesignLayer is updated correctly
    this.currentDesignLayer = pitLayer;
    if (pit.type === 'Round Pit') {
      drawCircleBase(mapView, this.newDesignForm, projectSR);
    } else {
      drawRectangularBase(mapView, this.newDesignForm, projectSR);
    }
    putStructureRepOnTop(mapView);
    return pitLayer;
  }

  @action addDikedPitToMap(dikedPit: any, mapView: MapView, isCurrentDesignLayer: boolean, projectSR: SpatialReference, id?: string): Layer {
    const dikedPitLayer = this.createPitLayer(dikedPit, mapView, projectSR, id);
    if (id) {
      dikedPitLayer.id = id;
    }
    mapView.map.add(dikedPitLayer);
    if (isCurrentDesignLayer) {
      this.currentDesignLayer = dikedPitLayer;
      if (id) {
        const pitEntry = (this.pitSummaryTable as any[]).find((entry) => entry.designElementId === id);
        if (pitEntry && pitEntry.checked) this.pitLayers.push(dikedPitLayer);
      }
    } else {
      this.dikedPitLayers.push(dikedPitLayer);
    }

    putStructureRepOnTop(mapView);
    return dikedPitLayer;
  }

  @action.bound removeAllPitLayers(view: MapView) {
    while (this.pitLayers.length > 0) {
      const pitLayer = this.pitLayers.shift();
      if (pitLayer) {
        view!.map.remove(pitLayer);
      }
    }
    this.pitLayers = [];
  }

  public createPitLayer(pit: any, mapView: MapView, projectSR: SpatialReference, elementId, label?: any): GroupLayer {
    // console.log('create Pit Layer : pit ', pit, ' and pit type : ', pit.type);

    if (pit.type === 'Rectangular Pit') {
      const wkid: number = isNaN(parseInt(pit.wkid)) ? this._currentWkid : parseInt(pit.wkid);
      const inputSR: SpatialReference = projectSR;
      const outputSR: SpatialReference = mapView.spatialReference;
      const inputUnit: LinearUnit = 'intl-feet';
      const outputUnit: LinearUnit = 'meters';
      const layer = constructRectPitLayer(pit, inputSR, outputSR, inputUnit, outputUnit, elementId, label);
      return layer;
    } else if (pit.type === 'Round Pit') {
      const wkid: number = isNaN(parseInt(pit.wkid)) ? this._currentWkid : parseInt(pit.wkid);
      const inputSR: SpatialReference = projectSR;
      const outputSR: SpatialReference = mapView.spatialReference;
      const inputUnit: LinearUnit = 'intl-feet';
      const outputUnit: LinearUnit = 'meters';
      const layer = constructRoundPitLayer(pit, inputSR, outputSR, inputUnit, outputUnit, elementId, label);
      return layer;
    } else if (pit.type === 'Diked Pit') {
      // console.log('dike pit :', pit);
      const wkid: number = isNaN(parseInt(pit.wkid)) ? this._currentWkid : parseInt(pit.wkid);
      const inputSR: SpatialReference = projectSR;
      const outputSR: SpatialReference = mapView.spatialReference;
      const inputUnit: LinearUnit = 'intl-feet';
      const outputUnit: LinearUnit = 'meters';
      const layer = constructDikedPitLayer(pit, inputSR, outputSR, inputUnit, outputUnit, elementId, label);
      return layer;
      //const dikedPitLayer = this.createDikedPitLayer(pit);
      //return dikedPitLayer;
    } else {
      const wkid: number = parseInt(pit.wkid);
      const inputSR: SpatialReference = projectSR;
      const outputSR: SpatialReference = mapView.spatialReference;
      const inputUnit: LinearUnit = 'intl-feet';
      const outputUnit: LinearUnit = 'meters';
      const layer = new GroupLayer();
      return layer;
    }
    // if (pit.type === 'Diked Pit') {
    //   const dikedPitLayer = this.createDikedPitLayer(pit, mapView);
    //   return dikedPitLayer;
    // } else {
    //   const pitBorderPointsLayer = this.createPitBorderLayer(pit);

    //   const pitPitBorderBoundaryLayer = createPointListBoundaryLayer(pitBorderPointsLayer);
    //   const pitVertexPointsLayer = this.createPitVertexLayer(pit);
    //   const pitVertexBoundaryLayer = createPointListBoundaryLayer(pitVertexPointsLayer);

    //   const pitRaysPointsLayer = this.createPitRaysLayer(pit);
    //   // comment slope arrows out for now until they are fixed
    //   // const pitSlopeDirectionLayer = this.createLayerPitSlopeDirections(pit);

    //   const pitSlopeArrowsLayer = this.createSlopeArrowsLayer(pit, mapView);

    //   const pitGroupLayer = new GroupLayer({
    //     layers: [
    //       pitVertexBoundaryLayer,
    //       pitVertexPointsLayer,
    //       pitRaysPointsLayer,
    //       // pitSlopeDirectionLayer,
    //       pitPitBorderBoundaryLayer,
    //       pitBorderPointsLayer,
    //       pitSlopeArrowsLayer,
    //     ],
    //   });

    //   pitVertexPointsLayer.title = 'Pit Vertices';
    //   pitVertexBoundaryLayer.title = 'Pit Vertices Boundary';
    //   pitRaysPointsLayer.title = 'Pit Rays';
    //   // pitSlopeDirectionLayer.title = 'Pit Slope Directions';
    //   pitBorderPointsLayer.title = 'Pit Border Points';
    //   pitPitBorderBoundaryLayer.title = 'Pit Border Boundary';
    //   pitGroupLayer.title = pit.name;
    //   return pitGroupLayer;
    // }
  }
  // public createDikedPitLayer(dikedPit: any, mapView: MapView): GroupLayer {
  //   const dikedPitPadBorderPointsLayer = this.createDikedPitPadBorderLayer(dikedPit);

  //   const dikedPitPadBorderBoundaryLayer = createPointListBoundaryLayer(dikedPitPadBorderPointsLayer);
  //   const dikedPitPadVertexPointsLayer = this.createDikedPitPadVertexLayer(dikedPit);
  //   const dikedPitPadVertexBoundaryLayer = createPointListBoundaryLayer(dikedPitPadVertexPointsLayer);
  //   const dikedPitPitBorderPointsLayer = this.createDikedPitPitBorderLayer(dikedPit);
  //   const dikedPitPitBorderBoundaryLayer = createPointListBoundaryLayer(dikedPitPitBorderPointsLayer);
  //   const dikedPitPitVertexPointsLayer = this.createDikedPitPitVertexLayer(dikedPit);
  //   const dikedPitPitVertexBoundaryLayer = createPointListBoundaryLayer(dikedPitPitVertexPointsLayer);
  //   const dikedPitPadRaysPointsLayer = this.createDikedPitPadRaysLayer(dikedPit);
  //   // commenting out arrows until they are fixed
  //   // const dikedPitPadSlopeDirectionLayer = this.createLayerDikedPitPadSlopeDirections(dikedPit);
  //   const dikedPitPitRaysPointsLayer = this.createDikedPitPitRaysLayer(dikedPit);
  //   // const dikedPitPitSlopeDirectionLayer = this.createLayerDikedPitPitSlopeDirections(dikedPit);

  //   const pitSlopeArrowsLayer = this.createSlopeArrowsLayer(dikedPit, mapView);

  //   const dikedPitGroupLayer = new GroupLayer({
  //     layers: [
  //       dikedPitPadVertexBoundaryLayer,
  //       dikedPitPadVertexPointsLayer,
  //       dikedPitPadRaysPointsLayer,
  //       // dikedPitPadSlopeDirectionLayer,
  //       dikedPitPadBorderBoundaryLayer,
  //       dikedPitPadBorderPointsLayer,

  //       dikedPitPitVertexBoundaryLayer,
  //       dikedPitPitVertexPointsLayer,
  //       dikedPitPitRaysPointsLayer,
  //       // dikedPitPitSlopeDirectionLayer,
  //       dikedPitPitBorderBoundaryLayer,
  //       dikedPitPitBorderPointsLayer,
  //       pitSlopeArrowsLayer,
  //     ],
  //   });

  //   dikedPitPadVertexPointsLayer.title = 'Diked Pit Pad Vertices';
  //   dikedPitPadVertexBoundaryLayer.title = 'Diked Pit Pad Vertices Boundary';
  //   dikedPitPadRaysPointsLayer.title = 'Dike Pit Pad Rays';
  //   // dikedPitPadSlopeDirectionLayer.title = 'Diked Pit Pad Slope Directions';
  //   dikedPitPadBorderPointsLayer.title = 'Diked Pit Pad Border Points';
  //   dikedPitPadBorderBoundaryLayer.title = 'Diked Pit Pad Border Boundary';
  //   dikedPitGroupLayer.title = 'Test Diked Pit';

  //   return dikedPitGroupLayer;
  // }
  private createPitBorderLayer(pit: any, projectSR: SpatialReference): FeatureLayer {
    let borders = pit.pitBorders;
    if (!borders) {
      borders = pit.borders;
    }
    // console.log('borders:', borders);
    const featureSet = this.createPitPointsFeatureSet(borders, projectSR);
    const source = featureSet.features;

    // create feature layer
    const markerSymbol = new SimpleMarkerSymbol({
      color: [255, 0, 0],
      size: 6,
      outline: {
        color: [0, 0, 0],
        width: 0.5,
      },
    });

    const pointlayer = new FeatureLayer({
      source,
      spatialReference: featureSet.spatialReference,
      renderer: new SimpleRenderer({ symbol: markerSymbol }),
      fields: featureSet.fields,
      objectIdField: featureSet.fields.filter((field) => {
        return field.type === 'oid';
      })[0].name,
    });

    return pointlayer;
  }

  // private createPointListBoundaryLayer(pointLayer: FeatureLayer): GraphicsLayer {
  //   const points: any[] = pointLayer.source.toArray().map((graphic) => {
  //     return graphic.geometry;
  //   });

  //   //close the ring
  //   points.push(points[0]);

  //   const convexSymbol = new SimpleFillSymbol({
  //     style: 'none',
  //     outline: {
  //       color: 'black',
  //       width: 2,
  //     },
  //   });

  //   const polygon = new Polygon();
  //   polygon.addRing(points);
  //   polygon.spatialReference = points[0].spatialReference;

  //   const graphic = new Graphic({ geometry: polygon, symbol: convexSymbol });
  //   const layer = new GraphicsLayer();
  //   layer.add(graphic);

  //   return layer;
  // }

  private createPitVertexLayer(pit: any, projectSR: SpatialReference): FeatureLayer {
    let vertices = pit.pitVertices;
    if (!vertices) {
      vertices = pit.vertices;
    }
    const featureSet = this.createPitPointsFeatureSet(vertices, projectSR);
    const source = featureSet.features;

    // create feature layer
    const markerSymbol = new SimpleMarkerSymbol({
      color: [255, 0, 0],
      size: 6,
      outline: {
        color: [0, 0, 0],
        width: 0.5,
      },
    });

    const pointlayer = new FeatureLayer({
      source,
      spatialReference: featureSet.spatialReference,
      renderer: new SimpleRenderer({ symbol: markerSymbol }),
      fields: featureSet.fields,
      objectIdField: featureSet.fields.filter((field) => {
        return field.type === 'oid';
      })[0].name,
    });

    return pointlayer;
  }

  private createPitRaysLayer(pit: any, projectSR: SpatialReference): FeatureLayer {
    const featureSet = this.createPitRaysFeatureSet(pit.rays, projectSR);
    const source = featureSet.features;

    // create feature layer
    const symbol = new SimpleLineSymbol({
      color: 'black',
      width: 0.5,
    });

    const layer = new FeatureLayer({
      source,
      spatialReference: featureSet.spatialReference,
      renderer: new SimpleRenderer({ symbol: symbol }),
      fields: featureSet.fields,
      objectIdField: featureSet.fields.filter((field) => {
        return field.type === 'oid';
      })[0].name,
    });

    return layer;
  }

  private createSlopeArrowsLayer(
    structure:
      | eftSetApi.RectangularPitResponse
      | eftSetApi.RoundPitResponse
      | eftSetApi.DikedPitResponse
      | eftSetApi.PadResponse,
    mapView: MapView,
    projectSR: SpatialReference
  ): GraphicsLayer {
    const spatialReference = projectSR;

    const slopeArrowsLayer = new GraphicsLayer({
      title: 'Slope Arrows', // put land level name here
    });
    const symbol = {
      type: 'simple-line', // autocasts as new SimpleLineSymbol()
      color: 'red',
      width: '2px',
      style: 'solid',
    } as any;

    if (structure.slopeArrows) {
      for (const slopeArrow of structure.slopeArrows) {
        const startPoint = new Point();
        startPoint.spatialReference = spatialReference;
        startPoint.x = slopeArrow.startPoint!.x!;
        startPoint.y = slopeArrow.startPoint!.y!;
        startPoint.z = slopeArrow.startPoint!.z!;

        const endPoint = new Point();
        endPoint.spatialReference = spatialReference;
        endPoint.x = slopeArrow.endPoint!.x!;
        endPoint.y = slopeArrow.endPoint!.y!;
        endPoint.z = slopeArrow.endPoint!.z!;

        const polyline = new Polyline();
        polyline.spatialReference = spatialReference;
        polyline.addPath([startPoint, endPoint]);

        // must project to view SR, otherwise 2D linesymbol will not show up in SceneView.
        const polyline2 = esriProjection.project(polyline, mapView.spatialReference) as Polyline;

        const graphic = new Graphic();

        graphic.geometry = polyline2;
        graphic.symbol = symbol;

        slopeArrowsLayer.graphics.add(graphic);
      }
    }
    return slopeArrowsLayer;
  }

  private createDikedPitPadBorderLayer(dikedPit: any, projectSR: SpatialReference): FeatureLayer {
    const featureSet = this.createDikedPitPadPointsFeatureSet(dikedPit.padBorders, projectSR);
    const source = featureSet.features;

    // create feature layer
    const markerSymbol = new SimpleMarkerSymbol({
      color: [255, 0, 0],
      size: 6,
      outline: {
        color: [0, 0, 0],
        width: 0.5,
      },
    });

    const pointlayer = new FeatureLayer({
      source,
      spatialReference: featureSet.spatialReference,
      renderer: new SimpleRenderer({ symbol: markerSymbol }),
      fields: featureSet.fields,
      objectIdField: featureSet.fields.filter((field) => {
        return field.type === 'oid';
      })[0].name,
    });

    return pointlayer;
  }

  private createDikedPitPadVertexLayer(dikedPit: any, projectSR: SpatialReference): FeatureLayer {
    const featureSet = this.createDikedPitPadPointsFeatureSet(dikedPit.padVertices, projectSR);
    const source = featureSet.features;

    // create feature layer
    const markerSymbol = new SimpleMarkerSymbol({
      color: [255, 0, 0],
      size: 6,
      outline: {
        color: [0, 0, 0],
        width: 0.5,
      },
    });

    const pointlayer = new FeatureLayer({
      source,
      spatialReference: featureSet.spatialReference,
      renderer: new SimpleRenderer({ symbol: markerSymbol }),
      fields: featureSet.fields,
      objectIdField: featureSet.fields.filter((field) => {
        return field.type === 'oid';
      })[0].name,
    });

    return pointlayer;
  }

  private createDikedPitPitBorderLayer(dikedPit: any, projectSR: SpatialReference): FeatureLayer {
    const featureSet = this.createDikedPitPitPointsFeatureSet(dikedPit.pitBorders, projectSR);
    const source = featureSet.features;

    // create feature layer
    const markerSymbol = new SimpleMarkerSymbol({
      color: [255, 0, 0],
      size: 6,
      outline: {
        color: [0, 0, 0],
        width: 0.5,
      },
    });

    const pointlayer = new FeatureLayer({
      source,
      spatialReference: featureSet.spatialReference,
      renderer: new SimpleRenderer({ symbol: markerSymbol }),
      fields: featureSet.fields,
      objectIdField: featureSet.fields.filter((field) => {
        return field.type === 'oid';
      })[0].name,
    });

    return pointlayer;
  }

  private createDikedPitPitVertexLayer(dikedPit: any, projectSR: SpatialReference): FeatureLayer {
    const featureSet = this.createDikedPitPitPointsFeatureSet(dikedPit.pitVertices, projectSR);
    const source = featureSet.features;

    // create feature layer
    const markerSymbol = new SimpleMarkerSymbol({
      color: [255, 0, 0],
      size: 6,
      outline: {
        color: [0, 0, 0],
        width: 0.5,
      },
    });

    const pointlayer = new FeatureLayer({
      source,
      spatialReference: featureSet.spatialReference,
      renderer: new SimpleRenderer({ symbol: markerSymbol }),
      fields: featureSet.fields,
      objectIdField: featureSet.fields.filter((field) => {
        return field.type === 'oid';
      })[0].name,
    });
    return pointlayer;
  }

  private createDikedPitPadRaysLayer(dikedPit: any, projectSR: SpatialReference): FeatureLayer {
    const featureSet = this.createDikedPitPadRaysFeatureSet(dikedPit.padRays, projectSR);
    const source = featureSet.features;

    // create feature layer
    const symbol = new SimpleLineSymbol({
      color: 'black',
      width: 0.5,
    });

    const layer = new FeatureLayer({
      source,
      spatialReference: featureSet.spatialReference,
      renderer: new SimpleRenderer({ symbol: symbol }),
      fields: featureSet.fields,
      objectIdField: featureSet.fields.filter((field) => {
        return field.type === 'oid';
      })[0].name,
    });

    return layer;
  }

  private createDikedPitPitRaysLayer(dikedPit: any, projectSR: SpatialReference): FeatureLayer {
    const featureSet = this.createDikedPitPitRaysFeatureSet(dikedPit.pitRays, projectSR);
    const source = featureSet.features;

    // create feature layer
    const symbol = new SimpleLineSymbol({
      color: 'black',
      width: 0.5,
    });

    const layer = new FeatureLayer({
      source,
      spatialReference: featureSet.spatialReference,
      renderer: new SimpleRenderer({ symbol: symbol }),
      fields: featureSet.fields,
      objectIdField: featureSet.fields.filter((field) => {
        return field.type === 'oid';
      })[0].name,
    });

    return layer;
  }

  private createPitPointsFeatureSet(pitPoints: any[], projectSR: SpatialReference): FeatureSet {
    const spatialReference = projectSR;

    const graphics = pitPoints.map((r, i) => {
      return new Graphic({
        geometry: new Point({
          spatialReference: spatialReference,
          hasZ: true,
          x: r.x,
          y: r.y,
          z: r.z,
        }),
        attributes: {
          ObjectID: i,
          pointName: r.pointName,
          y: r.y,
          x: r.x,
          z: r.z,
          description: r.description,
          // id: r.id,
          // visible: r.visible,
          // tinable: r.tinable,
          // editing_status: r.editing_status,
        },
      });
    });

    const fields = commonFields;


    const featureSet = new FeatureSet({
      features: graphics,
      fields: fields,
      geometryType: 'point',
      spatialReference: spatialReference,
      displayFieldName: 'pointName',
    });

    return featureSet;
  }
  private createPitRaysFeatureSet(rays: any[], projectSR: SpatialReference): FeatureSet {
    const spatialReference = projectSR;

    const graphics = !rays
      ? []
      : rays.map((ray, i) => {
        const point1 = new Point({
          spatialReference: spatialReference,
          hasZ: true,
          x: ray[0].x,
          y: ray[0].y,
          z: ray[0].z,
        });

        const point2 = new Point({
          spatialReference: spatialReference,
          hasZ: true,
          x: ray[1].x,
          y: ray[1].y,
          z: ray[1].z,
        });

        const polyline = new Polyline({
          spatialReference: spatialReference,
          hasZ: true,
        });
        polyline.addPath([point1, point2]);

        const graphic = new Graphic({
          geometry: polyline,
          attributes: {
            ObjectID: i,
          },
        });
        return graphic;
      });

    const fields: any = [
      {
        name: 'ObjectID',
        alias: 'ObjectID',
        type: 'oid',
      },
    ];

    const featureSet = new FeatureSet({
      features: graphics,
      fields: fields,
      geometryType: 'polyline',
      spatialReference: spatialReference,
      displayFieldName: 'ObjectID',
    });

    return featureSet;
  }

  private createDikedPitPitPointsFeatureSet(dikedPitPitPoints: any[], projectSR: SpatialReference): FeatureSet {
    const spatialReference = projectSR;

    const graphics = dikedPitPitPoints.map((r, i) => {
      return new Graphic({
        geometry: new Point({
          spatialReference: spatialReference,
          hasZ: true,
          x: r.x,
          y: r.y,
          z: r.z,
        }),
        attributes: {
          ObjectID: i,
          pointName: r.pointName,
          y: r.y,
          x: r.x,
          z: r.z,
          description: r.description,
          // id: r.id,
          // visible: r.visible,
          // tinable: r.tinable,
          // editing_status: r.editing_status,
        },
      });
    });

    const fields = commonFields;

    const featureSet = new FeatureSet({
      features: graphics,
      fields: fields,
      geometryType: 'point',
      spatialReference: spatialReference,
      displayFieldName: 'pointName',
    });

    return featureSet;
  }

  private createDikedPitPadPointsFeatureSet(dikedPitPadPoints: any[], projectSR: SpatialReference): FeatureSet {
    const spatialReference = projectSR;

    const graphics = dikedPitPadPoints.map((r, i) => {
      return new Graphic({
        geometry: new Point({
          spatialReference: spatialReference,
          hasZ: true,
          x: r.x,
          y: r.y,
          z: r.z,
        }),
        attributes: {
          ObjectID: i,
          pointName: r.pointName,
          y: r.y,
          x: r.x,
          z: r.z,
          description: r.description,
          // id: r.id,
          // visible: r.visible,
          // tinable: r.tinable,
          // editing_status: r.editing_status,
        },
      });
    });

    const fields = commonFields;

    const featureSet = new FeatureSet({
      features: graphics,
      fields: fields,
      geometryType: 'point',
      spatialReference: spatialReference,
      displayFieldName: 'pointName',
    });

    return featureSet;
  }

  private createDikedPitPadRaysFeatureSet(rays: any[], projectSR: SpatialReference): FeatureSet {
    const spatialReference = projectSR;

    const graphics = !rays
      ? []
      : rays.map((ray, i) => {
        const point1 = new Point({
          spatialReference: spatialReference,
          hasZ: true,
          x: ray[0].x,
          y: ray[0].y,
          z: ray[0].z,
        });

        const point2 = new Point({
          spatialReference: spatialReference,
          hasZ: true,
          x: ray[1].x,
          y: ray[1].y,
          z: ray[1].z,
        });

        const polyline = new Polyline({
          spatialReference: spatialReference,
          hasZ: true,
        });
        polyline.addPath([point1, point2]);

        const graphic = new Graphic({
          geometry: polyline,
          attributes: {
            ObjectID: i,
          },
        });
        return graphic;
      });

    //console.log('createPadRaysFeatureSet : graphic rays = ', graphics);

    const fields: any = [
      {
        name: 'ObjectID',
        alias: 'ObjectID',
        type: 'oid',
      },
    ];

    const featureSet = new FeatureSet({
      features: graphics,
      fields: fields,
      geometryType: 'polyline',
      spatialReference: spatialReference,
      displayFieldName: 'ObjectID',
    });

    return featureSet;
  }

  private createDikedPitPitRaysFeatureSet(rays: any[], projectSR: SpatialReference): FeatureSet {
    const spatialReference = projectSR;

    const graphics = !rays
      ? []
      : rays.map((ray, i) => {
        const point1 = new Point({
          spatialReference: spatialReference,
          hasZ: true,
          x: ray[0].x,
          y: ray[0].y,
          z: ray[0].z,
        });

        const point2 = new Point({
          spatialReference: spatialReference,
          hasZ: true,
          x: ray[1].x,
          y: ray[1].y,
          z: ray[1].z,
        });

        const polyline = new Polyline({
          spatialReference: spatialReference,
          hasZ: true,
        });
        polyline.addPath([point1, point2]);

        const graphic = new Graphic({
          geometry: polyline,
          attributes: {
            ObjectID: i,
          },
        });
        return graphic;
      });

    const fields: any = [
      {
        name: 'ObjectID',
        alias: 'ObjectID',
        type: 'oid',
      },
    ];

    const featureSet = new FeatureSet({
      features: graphics,
      fields: fields,
      geometryType: 'polyline',
      spatialReference: spatialReference,
      displayFieldName: 'ObjectID',
    });

    return featureSet;
  }
}

// PitDesignStore Interfaces
interface INewPit {
  pitName: string;
  pitType: string;
}

export interface INewPitForm {
  name: string;
  type: string;
  equalSlopes: boolean;
  equalCutSlopes: boolean;
  equalFillSlopes: boolean;
  equalSideSlopes: boolean;
  center: ICenterObject;
  width: number;
  length: number;
  orienAzimuth: number;
  radius: number;
  designElementId: number;
  topWidth: number;
  topElevation: number;
  bottomWidth: number;
  bottomElevation: number;
  bottomLength: number;
  pitDepth: number;
  slopes: number[];
  cutSlopes: number[];
  fillSlopes: number[];
  sideSlopes: number[];
}

interface ICenterObject {
  pointName: string;
  x: number;
  y: number;
  z: number;
}

interface IPendingEdits {
  name: boolean; // Name does not require redesign before accept
  design: boolean; // numerical inputs do require redesign before accept
  unsavedEdits: boolean; //UnsavedEdits is used for the user leaving warning pop up
}
